import { useState } from 'react';
import { getSubtableRowNumber } from './subtableColumns';

/** A row open in the menu, with its 1-based position for the title. */
export interface SubtableRow {
  entityId: string;
  number: number;
}

type NewTarget = { type: 'new' };
type EditTarget = { type: 'edit'; row: SubtableRow };
type RowMenuState = { isOpen: boolean; target: NewTarget | EditTarget };

export interface SubtableRowMenuState {
  isOpen: boolean;
  row: SubtableRow | null;
  selectedRowId: string | undefined;
  openNew: () => void;
  openRow: (entityId: string, index: number) => void;
  close: () => void;
}

/**
 * Open state for one surface's row menu: adding a row or editing one. Closing keeps the last
 * target, so the panel keeps showing that form while it animates out instead of flashing the new
 * row form.
 */
export function useSubtableRowMenu(): SubtableRowMenuState {
  const [{ isOpen, target }, setState] = useState<RowMenuState>({
    isOpen: false,
    target: { type: 'new' },
  });
  const row = target.type === 'edit' ? target.row : null;
  return {
    isOpen,
    /** The row the menu shows, kept through the close animation. */
    row,
    /** The row to highlight in the table: only while the menu is open. */
    selectedRowId: isOpen ? row?.entityId : undefined,
    openNew: () => setState({ isOpen: true, target: { type: 'new' } }),
    openRow: (entityId: string, index: number) =>
      setState({
        isOpen: true,
        target: { type: 'edit', row: { entityId, number: getSubtableRowNumber(index) } },
      }),
    close: () => setState(state => ({ ...state, isOpen: false })),
  };
}
